import _ from 'lodash';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import EBPropTypes from 'js-utils/prop-types';
import lazyGettext from 'js-utils/lazy-gettext';
import classNames from 'classnames';
import { FeatureInfo } from './_internal/features';
import Button from 'eventbrite_design_system/button/Button';
import { FEATURES_PROP_TYPES } from '../constants/componentPropTypes';

import './featuresExpansion.scss';

const ICON_COLOR = 'grey-500';
const MOBILE_FEATURES_TO_SHOW = 4;
const CTA_LABEL = lazyGettext('Compare all features');

const FEATURE_NOT_AVAILABLE_WITH_PAYPAL = lazyGettext(
    'Feature not available with PayPal payment processing',
);

const _containsFeaturesWithPaypalFinePrint = (features) =>
    _.some(features, ({ paypalExcl }) => paypalExcl);

const LargeMediumScreenFeatureExpansion = ({
    isLarge,
    heading,
    featuresList,
    onCompare,
    hasPaypalFinePrint,
    processingFeesFinePrint,
}) => {
    const containerDivClassName = classNames(
        'marketing-features-expansion__container--large',
        'eds-l-pad-hor-6',
        'eds-l-pad-bot-6',
        {
            'eds-g-grid': isLarge,
        },
    );
    const sectionClassName = classNames({
        'eds-g-cell': isLarge,
        'eds-g-cell-4-12': isLarge,
        'eds-l-mar-left-2': !isLarge,
        'eds-l-pad-bot-2': !isLarge,
    });
    const headingClassName = classNames('eds-text-hs', 'eds-l-mar-right-2', {
        'eds-l-pad-all-3': isLarge,
        'eds-l-pad-hor-3': !isLarge,
    });
    const ctaClassName = classNames(
        'eds-text-bm',
        'eds-text-weight--heavy',
        'eds-l-pad-all-3',
        {
            'eds-l-mar-bot-2': !isLarge,
        },
    );
    const featuresListDivClassName = classNames({
        'eds-g-cell': isLarge,
        'eds-g-cell-8-12': isLarge,
    });
    const featuresListSectionClassName = classNames(
        'marketing-features-expansion__feature-list--large',
        'eds-l-pad-left-9',
    );

    let featuresExpansionFinePrint = null;

    if (hasPaypalFinePrint) {
        featuresExpansionFinePrint = (
            <div className="eds-text-bs eds-text-weight--light eds-text--center eds-l-pad-top-5">
                <p>{processingFeesFinePrint}</p>
                <sup>&dagger;</sup>
                {FEATURE_NOT_AVAILABLE_WITH_PAYPAL}
            </div>
        );
    } else {
        featuresExpansionFinePrint = (
            <div className="eds-text-bs eds-text-weight--light eds-text--center eds-l-pad-top-5">
                {processingFeesFinePrint}
            </div>
        );
    }

    return (
        <div className={containerDivClassName}>
            <div className="marketing-container__max-width">
                <section className={sectionClassName}>
                    <h1 className={headingClassName}>{heading}</h1>
                    <h1 className={ctaClassName}>
                        <Button style="anchor" onClick={onCompare}>
                            {CTA_LABEL}
                        </Button>
                    </h1>
                </section>
                <div className={featuresListDivClassName}>
                    <section className={featuresListSectionClassName}>
                        {featuresList}
                    </section>
                </div>
                {featuresExpansionFinePrint}
            </div>
        </div>
    );
};

const SmallScreenFeatureExpansion = ({
    heading,
    featuresList,
    onCompare,
    hasPaypalFinePrint,
    processingFeesFinePrint,
}) => {
    const reducedFeaturesList = _.take(featuresList, MOBILE_FEATURES_TO_SHOW);
    const headingClassName = classNames(
        'marketing-features-expansion__header--small',
        'eds-text-hs',
        'eds-l-pad-all-6',
        'eds-l-mar-right-2',
    );
    const ctaClassName = classNames(
        'marketing-features-expansion__read-more-CTA',
        'eds-text-bm',
        'eds-text-weight--heavy',
        'eds-text--center',
        'eds-l-pad-top-2',
    );

    let featuresExpansionFinePrint = null;

    if (hasPaypalFinePrint) {
        featuresExpansionFinePrint = (
            <div className="eds-text-bs eds-text-weight--light eds-text--center eds-l-pad-top-6">
                <p>{processingFeesFinePrint}</p>
                <sup>&dagger;</sup>
                {FEATURE_NOT_AVAILABLE_WITH_PAYPAL}
            </div>
        );
    } else {
        featuresExpansionFinePrint = (
            <div className="eds-text-bs eds-text-weight--light eds-text--center eds-l-pad-top-5">
                {processingFeesFinePrint}
            </div>
        );
    }

    return (
        <div className="marketing-features-expansion__container--small">
            <section>
                <h1 className={headingClassName}>{heading}</h1>
                <section className="eds-l-pad-left-9">
                    {reducedFeaturesList}
                </section>
            </section>
            <h1 className={ctaClassName}>
                <Button style="anchor" onClick={onCompare}>
                    {CTA_LABEL}
                </Button>
            </h1>
            {featuresExpansionFinePrint}
        </div>
    );
};

const _getFeatureList = (features, isLargeScreen) => {
    let featuresList = null;
    const className = classNames({
        'marketing-features-expansion__feature--large': isLargeScreen,
        'eds-l-pad-hor-3 eds-l-pad-bot-3': isLargeScreen,
        'eds-l-pad-hor-4': !isLargeScreen,
    });

    if (features) {
        featuresList = features.map(
            ({ heading, description, iconType, paypalExcl }) => (
                <div key={`${heading.toString()}`} className={className}>
                    <FeatureInfo
                        iconType={iconType}
                        iconColor={ICON_COLOR}
                        heading={heading}
                        paypalExcl={paypalExcl}
                        description={description}
                    />
                </div>
            ),
        );
    }

    return featuresList;
};

export default class FeaturesExpansion extends PureComponent {
    static propTypes = {
        ...FEATURES_PROP_TYPES,
        /*
        Main heading for the group of features
        */
        heading: EBPropTypes.translation,
        /*
        Action to take when CTA is clicked
        */
        onClick: PropTypes.func,
    };

    render() {
        const {
            heading,
            features,
            onClick,
            processingFeesFinePrint,
        } = this.props;

        if (!heading) {
            return null;
        }

        const hasPaypalFinePrint = _containsFeaturesWithPaypalFinePrint(
            features,
        );

        return (
            <div>
                <div className="eds-show-up-md">
                    <LargeMediumScreenFeatureExpansion
                        processingFeesFinePrint={processingFeesFinePrint}
                        hasPaypalFinePrint={hasPaypalFinePrint}
                        isLarge={true}
                        heading={heading}
                        featuresList={_getFeatureList(features, true)}
                        onCompare={onClick}
                    />
                </div>
                <div className="eds-show-mn">
                    <LargeMediumScreenFeatureExpansion
                        processingFeesFinePrint={processingFeesFinePrint}
                        hasPaypalFinePrint={hasPaypalFinePrint}
                        isLarge={false}
                        heading={heading}
                        featuresList={_getFeatureList(features, true)}
                        onCompare={onClick}
                    />
                </div>
                <div className="eds-show-down-sw">
                    <SmallScreenFeatureExpansion
                        processingFeesFinePrint={processingFeesFinePrint}
                        hasPaypalFinePrint={hasPaypalFinePrint}
                        heading={heading}
                        featuresList={_getFeatureList(features, false)}
                        onCompare={onClick}
                    />
                </div>
            </div>
        );
    }
}
